import { http } from '@/config/httpCommon';
import vueRouters from '@/router/index';
import Store from '@/store';
import { AppConstants } from "@/webComponents/wcOrderDetail/constants";
import VsToast from '@vuesimple/vs-toast';
import { AxiosError } from "axios";
import { find, forIn, intersection, startCase } from "lodash";
import 'tslib';
import { ServerErrorResponse, ServerErrorSuccessResponse, ServerExceptionData } from '@/interfaces/serverResponse';
import { h } from "vue";
import { fabric } from 'fabric';

const router: Record<any, any> = vueRouters

/*
* constants starts
* */
const BASE_URL = process.env.VUE_APP_BASE_URL;

const COMPOSITION_OPTIONS = ['source-over', 'source-in', 'source-out', 'source-atop', 'destination-over',
  'destination-in', 'destination-out', 'destination-atop', 'lighter', 'copy', 'xor', 'multiply', 'screen', 'overlay',
  'darken', 'lighten', 'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference', 'exclusion', 'hue',
  'saturation', 'color', 'luminosity']

const CustimooOrderFlowStatuses: any = {
  quote_requested: 'Quote Requested',
  quote_rejected: 'Quote Rejected',
  quote_provided: 'Quote Provided',
  submitted_for_factory_review: 'Submitted for Factory Review',
  order_cancel: 'Cancelled',
  order_approve: 'Marked to Factory',
  factory_approved: 'Factory Approved',
  factory_rejected: 'Factory Rejected',
  submitted_for_customer_review: 'Submitted for Customer Review',
  customer_approved: 'Customer Approved',
  customer_rejected: 'Customer Rejected',
  in_production: 'In Production',
  production_files_uploaded: 'In Production',
  shipped: 'Shipped',
  completed: 'Completed',
  pending_for_factory_assignment: 'Pending For Factory Assignment',
  on_hold: 'On Hold',
};

const activityStatus = {
  quote_requested: {
    title: "Quote Requested",
    message: "Quote requested.",
  },
  quote_updated: {
    title: "Quote Updated",
    message: "Quote updated by customer.",
  },
  quote_rejected: {
    title: "Quote Rejected",
    message: "Quote rejected.",
  },
  quote_provided: {
    title: "Quote provided",
    message: "Quote provided by merchant.",
  },
  submitted_for_factory_review: {
    title: "Order Created",
    message: "Your order has been created and is awaiting confirmation.",
  },
  order_approve: {
    title: "Order Confirmed, Pending Artwork",
    message: "Your order has been forwarded to the factory. They will review the logos and design for accuracy and quality, making any necessary adjustments for optimal printing.",
  },
  factory_approved: {
    title: "Artwork Approved, Pending Test Print",
    message: "The factory has approved your artwork. Expect a physical sample to be uploaded within 5-7 days.",
  },
  order_cancel: {
    title: "Order Cancelled",
    message: "You have cancelled the order.",
  },
  factory_rejected: {
    title: "Artwork Rejected",
    message: "You rejected the Artwork. Please wait for the customer to edit and submit the artwork again.",
  },
  submitted_for_customer_review: {
    title: "Design Sample Submitted",
    message: "<p>The factory has provided sample images. Once the test print(s) are approved, full production will begin.</p>\n" +
      "    <p><strong>Note:</strong> Any changes or comments before test print approval may delay production. Production starts after test print approval.</p>\n" +
      "    \n" +
      "    <ul>\n" +
      "        <li><strong>Sublimation Production Time:</strong> 3–4 weeks (including shipping)</li>\n" +
      "        <li><strong>Tackle Twill Production Time:</strong> 7–8 weeks (including shipping)</li>\n" +
      "    </ul>.",
  },
  customer_approved: {
    title: "Design Sample Approved",
    message: "Your sample has been approved. Production is now pending.",
  },
  customer_rejected: {
    title: "Design Sample Rejected",
    message: "Customer has rejected the design samples. Please read the comments and upload new design samples.",
  },
  production_files_uploaded: {
    title: "Production Files uploaded",
    message: "Production Files uploaded",
  },
  in_production: {
    title: "In Production",
    message: "<p>The factory has started manufacturing your products. <strong>Important:</strong> No further changes can be made at this stage.</p>\n" +
      "    \n" +
      "    <ul>\n" +
      "        <li><strong>Sublimation Production Time:</strong> 3–4 weeks (including shipping)</li>\n" +
      "        <li><strong>Tackle Twill Production Time:</strong> 7–8 weeks (including shipping)</li>\n" +
      "    </ul>",
  },
  quality_control: {
    title: "Quality Control",
    message: "Quality Control",
  },
  shipped: {
    title: "Order Shipped",
    message: "You shipped the products.",
  },
  completed: {
    title: "Order Completed",
    message: "This order has completed successfully.",
  },
  on_hold: {
    title: "Order On Hold",
    message: "This order is currently on hold.",
  },
  resume_order: { 
    title: "Order Resumed",
    message: "This order has been resumed.",
  }
}

/*
* constants ends
* */

/*
* Methods starts
* */
// src/utils/bulletToast.ts
type Variant = "success" | "error" | "info";

interface ShowOptions {
  timeout?: number; // ms
  containerId?: string;
}

const DEFAULT_TIMEOUT = 7000;

function ensureContainer(containerId = "bullet-toast-container") {
  let c = document.getElementById(containerId);
  if (!c) {
    c = document.createElement("div");
    c.id = containerId;
    Object.assign(c.style, {
      position: "fixed",
      top: "12px",
      right: "12px",
      zIndex: "99999",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      alignItems: "flex-end",
      pointerEvents: "none", // allow clicks through gaps
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(c);
  }
  return c;
}

function getVariantStyles(v: Variant) {
  switch (v) {
    case "success":
      return { border: "#22c55e", titleColor: "#065f46" }; // green
    case "info":
      return { border: "#3b82f6", titleColor: "#1e3a8a" }; // blue
    case "error":
    default:
      return { border: "#ef4444", titleColor: "#7f1d1d" }; // red
  }
}

export function showBulletToast(
  title: string,
  bullets: string[] = [],
  variant: Variant = "info",
  options?: ShowOptions
) {
  const timeout = options?.timeout ?? DEFAULT_TIMEOUT;
  const containerId = options?.containerId;
  const container = ensureContainer(containerId);

  const { border, titleColor } = getVariantStyles(variant);

  // root toast element
  const toast = document.createElement("div");
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.style.pointerEvents = "auto";
  Object.assign(toast.style, {
    minWidth: "280px",
    maxWidth: "420px",
    background: "#fff",
    color: "#0f172a",
    padding: "12px 12px 12px 14px",
    borderRadius: "8px",
    boxShadow: "0 6px 18px rgba(2,6,23,0.08)",
    borderLeft: `4px solid ${border}`,
    transform: "translateY(-6px)",
    opacity: "0",
    transition: "opacity 220ms ease, transform 220ms ease",
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
  } as Partial<CSSStyleDeclaration>);

  // content wrapper
  const content = document.createElement("div");
  content.style.flex = "1";

  // title
  const titleEl = document.createElement("div");
  titleEl.textContent = title;
  titleEl.style.fontWeight = "600";
  titleEl.style.marginBottom = bullets.length ? "8px" : "0";
  titleEl.style.color = titleColor;
  titleEl.style.fontSize = "14px";
  content.appendChild(titleEl);

  // bullets
  if (bullets.length) {
    const ul = document.createElement("ul");
    Object.assign(ul.style, {
      margin: "0",
      paddingLeft: "18px",
      fontSize: "13px",
      color: "#334155",
      lineHeight: "1.35",
    } as Partial<CSSStyleDeclaration>);
    bullets.forEach((b) => {
      const li = document.createElement("li");
      li.textContent = String(b);
      li.style.marginBottom = "4px";
      ul.appendChild(li);
    });
    content.appendChild(ul);
  }

  // close button
  const closeBtn = document.createElement("button");
  closeBtn.setAttribute("aria-label", "Dismiss notification");
  closeBtn.innerHTML = "✕";
  Object.assign(closeBtn.style, {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "12px",
    color: "#64748b",
    padding: "4px",
    lineHeight: "1",
    marginLeft: "6px",
  } as Partial<CSSStyleDeclaration>);
  closeBtn.addEventListener("click", () => removeToast(toast));

  toast.appendChild(content);
  toast.appendChild(closeBtn);
  container.appendChild(toast);

  // show (animated)
  // slight delay to let element be appended to DOM before transition
  requestAnimationFrame(() => {
    toast.style.opacity = "1";
    toast.style.transform = "translateY(0)";
  });

  // auto remove
  const autoRemove = window.setTimeout(() => {
    removeToast(toast);
  }, timeout);

  // cleanup function
  function removeToast(el: HTMLElement) {
    if (!el.parentElement) return;
    // cancel pending auto remove if any
    window.clearTimeout(autoRemove);
    el.style.opacity = "0";
    el.style.transform = "translateY(-6px)";
    // remove after transition
    setTimeout(() => {
      el.remove();
      // if container empty remove it
      if (container.childElementCount === 0 && container.parentElement) {
        container.remove();
      }
    }, 260);
  }

  // return a handle if caller wants to remove early
  return {
    dismiss: () => removeToast(toast),
  };
}


const handleResponseException = (errorResponse: AxiosError | TypeError) => {
  let title = "";
  let bullets: string[] = [];

  if ("isAxiosError" in errorResponse) {
    const responseData = (errorResponse as AxiosError).response?.data as any;
    title =
      responseData?.message ||
      (errorResponse as AxiosError).response?.statusText ||
      "Error occurred";

    if (responseData?.errors && Array.isArray(responseData.errors)) {
      // flatten nested arrays safely
      bullets = responseData.errors.flat(Infinity).map(String);
    }
  } else {
    title = (errorResponse as TypeError).message || "Unexpected error";
  }

  showBulletToast(title, bullets, "error", { timeout: 7000 });
};

const handleResponseErrors = (errorResponse: TypeError | ServerErrorResponse) => {
  let message = '';
  let errors = [];
  if("isAxiosError" in errorResponse) {
    const response = errorResponse.response as unknown as ServerErrorResponse;
    const responseData = response.data as ServerExceptionData | ServerErrorSuccessResponse;
    message = responseData.message;
    if("errors" in responseData) {
      errors = responseData.errors;
    }
  } else {
    message = errorResponse.message;
  }
  if ( AppConstants.isOrderDetailWebComponent === false) {
    if(message) {
      console.log('message', message)
      showToast(message, "ERROR");
    }
    if(errors && errors.length > 0) {
      console.log('errors', errors)
      errors.forEach((error) => showToast(error, "ERROR"));
    }
  }
}

const logData = (data: any) => {
  console.info("data logged", data)
}

const pathInfo = (file_path: string,) => {
  const pathArray = file_path.split("/");
  const lastIndex = pathArray.length - 1;
  const name = pathArray[lastIndex];
  const extension = name.substring(name.lastIndexOf(".") + 1);
  return {
    name: name, extension: extension
  };
}

const getFileExtensionType = (type: string, file_extension: string) => {
  const extensions: Record<any, any> = {
    raster: ['jpg', 'jpeg', 'png'],
    vector: ['svg', 'ai', 'eps', 'pdf']
  }
  const type_extensions = extensions[type];
  if (file_extension) {
    const extension_index = file_extension.lastIndexOf(".");
    if (extension_index > 0) {
      file_extension = file_extension.substring(extension_index + 1)
    }
    return type_extensions.includes(file_extension);
  }
  return type_extensions;
}

const urlToBase64 = (url: string) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
      if (xhr.status == 200) {
        const reader = new FileReader();
        reader.onloadend = function () {
          resolve(reader.result);
        }
        reader.readAsDataURL(xhr.response);
      } else {
        reject(`Error (status = ${xhr.status}, status text = ${xhr.statusText}) while getting file from url ${xhr.responseURL}`);
      }
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  });
}

const urlToBase64Backend = async (urls: string[]) => {
  const response = await http.post('url_to_base64', { file_urls: urls }).catch((errorResponse) => {
    console.error('Error while converting url to base64', errorResponse)
  })
  return response ? response.data.result.base64_files : []
}

const getPermissions = async () => {
  const permissions_response: any = await http.get(`${BASE_URL}/permissions`).catch(error => {
    handleResponseException(error)
    return false;
  })
  //in case of exception the catch block will run and constant "permission_response" will have return value from catch block that is false
  if (permissions_response) {
    const response_data = permissions_response.data
    await Store.dispatch("setAuthPermissions", response_data.result.permissions)

  }
}

/*
* @param permissions: string | array. In case o f string check if permission exists or not.
* @param all: boolean If it's true then it means to check all given permissions in the auth_permissions. If it's false then check if any one condition exists in auth_permissions
* */
function can(permissions: string | Array<any>, all = false) {
  const auth_permissions = Store.getters.permissions;
  if (permissions.constructor.name == "String") {
    return auth_permissions.includes(permissions)
  } else {
    const can_do = intersection(auth_permissions, permissions);
    if (can_do) {
      if (all) {
        return can_do.length == permissions.length;
      } else {
        return can_do.length > 0;
      }
    }
  }
}

const handleSaleRepLogin = (order_id, token, redirect_link) => {

  http.post(`login-salerep-user`, { 'order_id': order_id, auth_token: token }).then(async (successResponse) => {
    const response_data = successResponse.data;
    if (response_data.success) {
      // console.log(window.location.href);
      // window.open(`/`);
      await handleUserLoginLogOutAction('logout');
      await handleUserLoginLogOutAction('login', response_data.result);
      window.location.href = redirect_link;
    } else {
      window.location.href = '/login';
    }
  }).catch(error => {
    handleResponseException(error)
  });
}

const handleUserLoginLogOutAction = async (action = 'logout', login_response: Record<any, any> | null = null) => {
  if (action == 'login') {
    const auth_user = login_response ? login_response.user : null;
    setAuthUserData(auth_user);
    if (router.history.current.query.redirect) {
      const redirect_path = router.history.current.query.redirect;
      delete router.history.current.query.redirect
      await router.push({ path: redirect_path, query: router.history.current.query })
    } else {
      await router.push({ name: 'Dashboard' });
    }
  }
  else if (action == 'hardLogout') {

    http.get('logout').then(async response => {
      if (response.status == 200) {
        localStorage.removeItem('user');
        Store.commit('SET_AUTH_USER', null);
        localStorage.removeItem('company');
        localStorage.removeItem('role');
        Store.commit('SET_AUTH_ROLE', null);
        localStorage.removeItem('access_token');
        await Store.dispatch("setAuthPermissions", [])
        Store.commit('AUTH_SUCCESS', null)
        await router.push('login');
      }
    })
  }
  else {
    localStorage.removeItem('user');
    Store.commit('SET_AUTH_USER', null);
    localStorage.removeItem('company');
    localStorage.removeItem('role');
    Store.commit('SET_AUTH_ROLE', null);
    localStorage.removeItem('access_token');
    await Store.dispatch("setAuthPermissions", [])
    Store.commit('AUTH_SUCCESS', null)
    await router.push('login');
  }
}

const setAuthUserData = async (user: Record<any, any>) => {
  localStorage.setItem('company', JSON.stringify(user.company));
  localStorage.setItem('role', JSON.stringify(user.role));
  localStorage.setItem('access_token', user.access_token);
  Store.commit('SET_AUTH_ROLE', JSON.parse(JSON.stringify(user.role)));
  const auth_permissions = user.permissions;
  delete user.company;
  delete user.role;
  Store.commit('AUTH_SUCCESS', user.access_token)
  delete user.access_token;
  delete user.permissions;
  Store.commit('SET_AUTH_USER', user);
  localStorage.setItem('user', JSON.stringify(user));
  await Store.dispatch("setAuthPermissions", auth_permissions)
  if (Store.getters.permissions.includes('access-new-dashboard')) {
    window.location.href = `${process.env.VUE_APP_NEW_URL}/?access_token=${Store.getters.isAuthenticated}`;
  }
}

const showResponseErrors = (response: Record<any, any>) => {
  const toaster_errors = response.data.errors;
  if (toaster_errors.length == 0) {
    toaster_errors.push(response.data.message);
  }
  toaster_errors.forEach((error_message: []) => {
    VsToast.show({
      title: error_message,
      variant: 'error',
      timeout: 5000,
      position: "bottom-left"
    });
  })
}

/*
* variant = {success, warning, error, info, secondary}
* */
const showToast = (message: string, variant = "success") => {
  VsToast.show({
    title: message,
    timeout: 5000,
    position: "bottom-left",
    variant: variant
  });
}

const getCompositionsOptions = ({ key = 'code', label = 'label', only_options = [] } = {}) => {
  const composition_options = COMPOSITION_OPTIONS.map(composition => {
    const composition_label = startCase(composition.replace(/-./g, x => ' ' + x[1]))
    return { [key]: composition, [label]: composition_label }
  })
  const only_type = only_options.constructor.name
  if (only_type == 'String' && only_options) {
    return find(composition_options, [[key], only_options])
  }
  if (only_type == 'Array' && only_options.length > 0) {
    return composition_options.filter(composition_options => {
      return only_options.includes(composition_options[key])
    })
  }
  return composition_options
}

const setPageModal = (page, route_name, search = null, tab = null, per_page = null) => {
  const page_modal_object: any = {
    page: page,
    page_name: route_name,
    search: search,
    tab: tab,
  }
  if (per_page !== null) {
    page_modal_object.per_page = per_page
  }
  localStorage.setItem('page_modal', JSON.stringify(page_modal_object));
}

const getPageModal = (route_name = null) => {
  let page_modal = JSON.parse(localStorage.getItem('page_modal'));
  if (route_name) {
    if (!(page_modal && page_modal.page_name === route_name)) {
      page_modal = null
    }
  }
  return page_modal
}

const removePageModal = () => {
  localStorage.removeItem('page_modal');
}

const getPreviousPage = () => {
  const page_modal = getPageModal();
  if (page_modal && page_modal.page && parseInt(page_modal.page) !== 1) {
    return parseInt(page_modal.page) - 1;
  }
}
const getnextPage = () => {
  const page_modal = getPageModal();
  if (page_modal && page_modal.page && parseInt(page_modal.page) >= 1) {
    return parseInt(page_modal.page) + 1;
  }
}

const getLastPage = (per_page, total_records) => {
  if ((total_records % per_page) === 0) {
    return Math.ceil(total_records / per_page) + 1;
  } else {
    return Math.ceil(total_records / per_page);
  }

}

const mountedLogicPagination = async (page_attribute, search_attribute, search_field, routeName, listingCallFn) => {
  const product_modal = getPageModal();
  if (product_modal && product_modal.page && product_modal.page_name === routeName) {
    let params = `?${page_attribute}=${product_modal.page}`;
    if (product_modal.search) {
      search_field = product_modal.search
      params += `&${search_attribute}=${product_modal.search}`
    }
    await listingCallFn(params);
  }
  else {
    removePageModal()
    await listingCallFn('')
  }
}

const santaClone = (object: any) => {
  return JSON.parse(JSON.stringify(object))
}

const getAuthUser = () => {
  const get_user = localStorage.getItem("user")
  if (get_user) {
    const auth_user = JSON.parse(get_user)
    auth_user.role = JSON.parse(localStorage.getItem("role"))
    return auth_user
  } else {
    return null
  }
}

const updateObjectKeys = async (update_object, update_from_object) => {
  update_object = santaClone(update_object);
  update_from_object = santaClone(update_from_object);
  Object.keys(update_object).forEach(obj_key => {
    if (Object.prototype.hasOwnProperty.call(update_from_object, obj_key)) {
      update_object[obj_key] = update_from_object[obj_key]
    }
  });
  return update_object
}

const getMimeTypeForExtension = (extension) => {
  const extensions_with_mimetypes = {
    "svg": "image/svg+xml",
    "eps": "application/postscript",
    "pdf": "application/pdf",
    "dxf": "application/dxf",
    "png": "application/dxf"
  };
  return extensions_with_mimetypes[extension];
}
//file_string must be base64 encode string
const getBlobFromString = (file_string, file_extension) => {
  // Decode the base64-encoded PDF content
  const decode_file_string = atob(file_string);

  // Convert the decoded binary data into a Uint8Array
  const uint8Array = new Uint8Array(decode_file_string.length);
  for (let i = 0; i < decode_file_string.length; i++) {
    uint8Array[i] = decode_file_string.charCodeAt(i);
  }

  // Create a Blob object from the Uint8Array
  const blob = new Blob([uint8Array], { type: getMimeTypeForExtension(file_extension) });

  // Create a URL for the Blob object
  return URL.createObjectURL(blob);
}

//todo when changing props in this method make sure to update the same method in builder project helper.ts file
const getLogoUpdatedProps = (updated_logo: Record<any, any>) => {
  const updated_props: Record<any, any> = {
    id: updated_logo.id, url: updated_logo.url, original_logo: updated_logo.original_logo_url, original_logo_url: updated_logo.original_logo_url,
    transparent_logo: updated_logo.transparent_logo_url, smart_transparent_logo: updated_logo.smart_transparent_logo_url,
    is_smart_transparent: updated_logo.is_smart_transparent ? true : false, is_vector: updated_logo.is_vector ? true : false,
    logo_name: updated_logo.logo_name, is_replace_success: updated_logo.is_replace_success ? true : false,
    logo_colors: updated_logo.logo_colors
  }
  return updated_props
}

const fileToBase64 = (file: any) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = function () {
      const file_info = {
        base64String: reader.result,
        original_name: file.name,
        extension: file.name.split('.').pop()?.toLowerCase()
      }
      resolve(file_info);
    }
    reader.readAsDataURL(file);
  });

}

const unitConversion = (value: number, measurement_unit) => {
  if (measurement_unit) {
    switch (measurement_unit.conversion_operator) {
      case 'multiply':
        return { value: (value * (parseFloat(measurement_unit.conversion_value))).toFixed(1), unit: measurement_unit.unit }
        break;
      case 'divide':
        return { value: (value / (parseFloat(measurement_unit.conversion_value))).toFixed(1), unit: measurement_unit.unit }
        break;
      default: {
        const value_string = value ? value.toString() : '';
        return { value: parseFloat(value_string).toFixed(1), unit: measurement_unit.unit }
      }
    }
  }
  return { value: '0', unit: '' };
}

const getLogoDefaultObject = (default_values = {}) => {
  const default_obj = {
    id: null, product_id: null, product_style_id: null, following_product_ids: null, rotation: 0,
    originalWidth: 57, originalHeight: 57, width: 57, height: 57, name_of_placement: "chest", side: "front", x_axis: 300,
    y_axis: 300, is_locked: false, logo_name: null, original_logo: null, transparent_logo: null, smart_transparent_logo: null,
    original_logo_url: null, is_smart_transparent: null, url: null, haveControls: true, logo_colors: [], is_replace_success: false,
    logo_index: 0, is_vector: false, logos_follows_product: 0
  }
  return { ...default_obj, ...default_values }
}

const findActivityWithPosition = (activity_items, status, position) => {
  const submittedItems = activity_items.filter(item => item.status === status);
  // Return the second item if it exists, otherwise return the first
  if (submittedItems.length > position) {
    return submittedItems[position];
  } else if (submittedItems.length === 1) {
    return submittedItems[0];
  }
  return null; //
}

const getDomDocument = (parent_doc = false) => {
  if (parent_doc) {
    // return window.parent.document
    return getWindowObject().document
  }
  const dom_document = document.querySelector(getWebComponentNames())
  return dom_document ? dom_document?.shadowRoot : document
}
const getWindowObject = () => {
  try {
    return window.parent
  } catch (error) {
    return window
    console.info('Error while getting window object', error)
  }
}

const getWebComponentNames = (return_string = true): any => {
  if (return_string) {
    return "v-customizer, v-order-detail"
  } else {
    return ["v-customizer, v-order-detail"]
  }
}



const findActivity = (status_activities, status, length) => {
  const activities = status_activities.filter((activity_item) => activity_item.status === status);
  const activity = activities.find((activity_item) => {
    return activity_item.activity_items.length === length
  });
  if (activity) {
    return activity
  }
  else {
    return null;
  }
}
const mergeActivityArray = (current_array, artwork_created_activity_items, submitted_customer_activity_review = null) => {
  const existingFactoryIds = current_array.map(item => item.factory_product_id);
  const submittedActivityFactoryIds = submitted_customer_activity_review.activity_items.map(item => item.factory_product_id);
  const current_array_data = JSON.parse(JSON.stringify(current_array));
  const artwork_created_activity_items_data = JSON.parse(JSON.stringify(artwork_created_activity_items));

  const artwork_created_activity = artwork_created_activity_items_data.map((item: Record<any, any>) => {
    let activity_item = item;
    if (submittedActivityFactoryIds.includes(item.factory_product_id) && !existingFactoryIds.includes(item.factory_product_id)) {
      const submitted_activity_item = submitted_customer_activity_review.activity_items.find((activity_review_item: Record<any, any>) => activity_review_item.factory_product_id === item.factory_product_id);
      activity_item.message = submitted_activity_item?.message;
      activity_item.skip_customer_approval = submitted_activity_item?.skip_customer_approval;
    }
    else if (existingFactoryIds.includes(item.factory_product_id)) {
      const current_array_item = current_array_data.find((current_array_item: Record<any, any>) => current_array_item.factory_product_id === item.factory_product_id);
      activity_item = JSON.parse(JSON.stringify(current_array_item));
    }
    return activity_item
  });

  return artwork_created_activity

}

const roundOff = (num: number, digits = 2): number => {
  const factor = Math.pow(10, digits);
  const rounded = Math.round(num * factor) / factor;

  // If the number is an integer, return it without decimal part
  if (Number.isInteger(rounded)) {
    return rounded;
  }

  return Number(rounded.toFixed(digits).replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.$/, ''));
};

const checkIsEmpty = (obj: any) => {
  if (obj) {
    const obj_type = obj.constructor.name
    if (obj_type == "Array") {
      return obj.length == 0
    } else if (obj_type == "Object") {
      return Object.keys(obj).length == 0
    } else {
      return true
    }
  } else {
    return true;
  }
}

const getPaginationObject = (pagination_data_container: any = {}, updated_values = {}) => {
  let meta_obj = null
  const pagination_existing_info = getPageModal()
  let per_page = 10
  if (pagination_existing_info && pagination_existing_info.page_name === router.history.current.name && pagination_existing_info.per_page) {
    per_page = pagination_existing_info.per_page
  }
  const pagination_object = { current_page: 1, per_page: per_page, total: 10, last_page: 1 }
  const default_props = { ...{ limit: 11 }, ...updated_values }
  if (!checkIsEmpty(pagination_data_container)) {
    if (pagination_data_container.meta) {
      meta_obj = pagination_data_container.meta
    }
    if (meta_obj) {
      pagination_data_container = meta_obj
    }
    pagination_object.current_page = pagination_data_container.current_page
    pagination_object.per_page = pagination_data_container.per_page
    pagination_object.total = pagination_data_container.total
    pagination_object.last_page = pagination_data_container.last_page

  }
  return { ...pagination_object, ...default_props }
}

const getExtensionFromMimeType = (mimetype: string) => {
  if (mimetype) {
    const mimetypes_info = {
      'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/bmp': 'bmp', 'image/svg+xml': 'svg', 'application/pdf': 'pdf',
    };
    return mimetypes_info[mimetype.toLowerCase()] || '';
  } else {
    return ''
  }
}
const getRandom = (length = 5, type = 'number') => {
  let rand_string: number | string = "";
  if (type === 'number') {
    let max_number: number | string = "9";
    for (let i = 1; i < length; i++) {
      max_number = max_number + "9";
    }
    max_number = parseInt(max_number);
    rand_string = Math.floor(max_number + (Math.random() * max_number));
  } else if (type === 'string') {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    for (let i = 0; i < length; i++) {
      rand_string += chars.charAt(Math.floor(Math.random() * chars.length))
    }
  } else {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (let i = 0; i < length; i++) {
      rand_string += chars.charAt(Math.floor(Math.random() * chars.length))
    }
  }
  return rand_string;
}

const getDateTimeFormatted = () => {
  const pad = (num, size) => num.toString().padStart(size, '0');
  const date = new Date()

  const day = pad(date.getDate(), 2);
  const month = pad(date.getMonth() + 1, 2); // Months are zero-indexed
  const year = date.getFullYear();
  const hours = pad(date.getHours() % 12 || 12, 2); // 12-hour format
  const minutes = pad(date.getMinutes(), 2);
  const seconds = pad(date.getSeconds(), 2);
  const milliseconds = pad(date.getMilliseconds(), 3); // Milliseconds
  // Convert milliseconds to microseconds (approximation)
  const microseconds = pad(milliseconds * 1000, 6);
  return `${day}${month}${year}${hours}${minutes}${seconds}${microseconds}`
}

const base64ToFile = (base64String, isBase64String, fileName = '') => {
  let response_file: File | null = null
  if (isBase64String) {
    const mime_type_matched = base64String.match(/^data:(.*);base64,/);
    const in_valid_base64_string = base64String.includes(',')
    // Ensure the base64 string is valid
    if (mime_type_matched && in_valid_base64_string) {
      const mimetype: string = mime_type_matched[1];
      const file_extension = getExtensionFromMimeType(mimetype);
      if (!fileName) {
        const date_time_formatted = getRandom(5) + '_' + getDateTimeFormatted()
        fileName = `${date_time_formatted}.${file_extension}`
      }
      // Remove the prefix (if present)
      const base64Data = base64String.split(',')[1];

      // Decode the base64 string
      const byteString = atob(base64Data);

      // Create an array buffer
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const intArray = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
      }

      // Create a Blob and then a File
      const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
      response_file = new File([blob], fileName, { type: 'application/octet-stream' });
    }
  }
  else {
    const blob = new Blob([base64String], { type: 'application/octet-stream' });
    response_file = new File([blob], fileName, { type: 'application/octet-stream' });
  }
  return response_file;
}
const isBase64File = (str) => {
  return str && typeof str === 'string' && str.startsWith('data:') && str.includes('base64,');
}
const getDateRange = (option) => {
  const today = new Date();
  const isoDay = today.getDay() === 0 ? 7 : today.getDay(); // Adjust for ISO standard (Monday to Sunday)

  switch (option) {
    case 'this_week': {
      const startOfWeek = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0));
      startOfWeek.setDate(today.getDate() - isoDay + 1); // Monday
      return {
        from: startOfWeek,
        to: today,
      };
    }
    case 'last_week': {
      const lastWeekEnd = new Date(today);
      lastWeekEnd.setDate(today.getDate() - isoDay); // Last Sunday
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6); // Monday
      return {
        from: lastWeekStart,
        to: lastWeekEnd,
      };
    }
    // this month should be added after confirmation
    case 'this_month': {
      const startOfMonth = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0)); // First day of this month
      return {
        from: startOfMonth,
        to: today,
      };
    }
    case 'last_month': {
      const startOfLastMonth = new Date(Date.UTC(today.getFullYear(), today.getMonth() - 1, 1, 0, 0, 0, 0));
      const endOfLastMonth = new Date(Date.UTC(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999)); // Last day of last month
      return {
        from: startOfLastMonth,
        to: endOfLastMonth,
      };
    }
    case 'this_quarter': {
      const currentQuarter = Math.floor(today.getUTCMonth() / 3) + 1;
      const startOfQuarter = new Date(Date.UTC(today.getUTCFullYear(), (currentQuarter - 1) * 3, 1)); // First day of the quarter
      return {
        from: startOfQuarter,
        to: today,
      };
    }
    case 'last_quarter': {
      const currentQuarter = Math.floor(today.getUTCMonth() / 3) + 1;
      const startOfLastQuarter = new Date(Date.UTC(today.getUTCFullYear(), (currentQuarter - 2) * 3, 1)); // First day of last quarter
      const endOfLastQuarter = new Date(Date.UTC(today.getUTCFullYear(), (currentQuarter - 1) * 3, 0, 23, 59, 59, 999)); // Last day of last quarter

      if (currentQuarter === 1) {
        startOfLastQuarter.setUTCFullYear(today.getUTCFullYear() - 1);
        endOfLastQuarter.setUTCFullYear(today.getUTCFullYear() - 1);
      }

      return {
        from: startOfLastQuarter,
        to: endOfLastQuarter,
      };
    }
    case 'this_year': {
      return {
        from: new Date(Date.UTC(today.getUTCFullYear(), 0, 1)), // January 1
        to: today,
      };
    }
    case 'last_year': {
      const startOfLastYear = new Date(Date.UTC(today.getUTCFullYear() - 1, 0, 1)); // January 1 of last year
      const endOfLastYear = new Date(Date.UTC(today.getUTCFullYear() - 1, 11, 31, 23, 59, 59, 999)); // December 31 of last year
      return {
        from: startOfLastYear,
        to: endOfLastYear,
      };
    }
    default:
      throw new Error(`Unsupported option: ${option}`);
  }
}

const isEcommercePlatform = () => {
  const company = Store.getters.company;
  return ['shopify', 'wordpress', 'bigcommerce'].includes(company?.platform)
}

const getTechpackColorType = (
  techpack_color_response: Record<string, any> = {},
  svg_group = '',
  product_id: number | string | null = null
): string | null => {
  if (!product_id) {
    return null;
  }

  const product_color_data = techpack_color_response[String(product_id)];
  if (!product_color_data) {
    return null;
  }

  if (
    svg_group &&
    product_color_data.svg_group_color_container &&
    product_color_data.svg_group_color_container[svg_group]
  ) {
    return 'product_color';
  }

  return product_color_data.color_type ?? null;
};

const getTechpackIsCustomColorAllowed = (
  techpack_color_response: Record<string, any> = {},
  product_id: number | string | null = null
): boolean | null => {
  if (!product_id) {
    return null;
  }

  const product_color_data = techpack_color_response[String(product_id)];
  if (!product_color_data) {
    return null;
  }

  if (typeof product_color_data.is_custom_color_allowed === 'boolean') {
    return product_color_data.is_custom_color_allowed;
  }

  if (product_color_data.is_custom_color_allowed === 1) {
    return true;
  }

  if (product_color_data.is_custom_color_allowed === 0) {
    return false;
  }

  return null;
};

const extractDesignParts = (svgFile: string): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    let svgContent = svgFile;
    
    if (
      typeof svgContent !== 'string' ||
      !svgContent.includes('<svg') ||
      !svgContent.includes('</svg>')
    ) {
      resolve([]); // Return empty array if SVG is invalid
      return;
    }

    // Clean up SVG content
    svgContent = svgContent.replace(/xmlns="&ns_sfw;"/g, 'xmlns=""');
    
    const designParts: string[] = [];
    
    // Use Fabric.js to load and parse SVG
    try {
      fabric.loadSVGFromString(svgContent, (objects: fabric.Object[], options: any) => {
        if (!objects || objects.length === 0) {
          resolve(designParts);
          return;
        }
        // Simply loop through first level objects
        objects.forEach((item: Record<any, any>) => {
          item.set('id', item.id.split('_')[0])
          item.id = item.id.toLowerCase()
          
          // Check if object has an ID and fill attribute
          if (item.id && item.fill) {
            if (!item.id.includes('noncustomizable') && !item.id.includes('inside') && !item.id.includes('anchor') && !designParts.includes(item.id)) {
              if (item.fill && item.fill.gradientUnits) {
                item.fill.colorStops.forEach((color_stop: Record<any, any>, index: number) => {
                  const gradientDesignPartId = `${item.id}_${index + 1}`;
                  if (!designParts.includes(gradientDesignPartId)) {
                    designParts.push(gradientDesignPartId);
                  }
                })
              } else {
                designParts.push(item.id)
              }
            }
          }
        });

        resolve(designParts);
      })
    } catch (error) {    
        resolve([]); // Return empty array on error
    }
  });
}

const setPageTitle = (title = ''): void => {
  const domDocument = window.parent.document;
  domDocument.title = `${process.env.VUE_APP_NAME}  ${title}`;
}

/*
* Methods ends
* */

export {
  activityStatus, base64ToFile, BASE_URL, can, checkIsEmpty, COMPOSITION_OPTIONS, CustimooOrderFlowStatuses, fileToBase64,
  findActivity, findActivityWithPosition, getAuthUser, getBlobFromString, getCompositionsOptions, getDateRange, getDomDocument,
  getFileExtensionType, getLastPage, getLogoDefaultObject, getLogoUpdatedProps, getMimeTypeForExtension, getnextPage, getPageModal,
  getPaginationObject, getPermissions, getPreviousPage, getRandom, handleResponseException, handleResponseErrors, handleSaleRepLogin,
  handleUserLoginLogOutAction, isBase64File, isEcommercePlatform, getTechpackColorType, getTechpackIsCustomColorAllowed,
  logData, mergeActivityArray, mountedLogicPagination,
  pathInfo, removePageModal, roundOff, santaClone, setAuthUserData, setPageModal, showResponseErrors, showToast, setPageTitle, unitConversion,
  updateObjectKeys, urlToBase64, urlToBase64Backend, extractDesignParts
};

